import { pickT } from "@repo/shared";
import { useBrandColor } from "@/lib/context/brand-color-context";
import { getRelativeTime } from "@/lib/utils";
import { T } from "./equity-style-grid.i18n";

type EquityStyleGridProps = {
  data: Array<{
    style: string;
    percentage: number;
  }>;
  timestamp?: string;
  language?: string;
  coverage?: {
    totalProducts: number;
    coveredProducts: number;
    originalWeightPct: number;
  };
  /** Hide the weight legend — used by the followup comparison, which shows two grids side by side. */
  showLegend?: boolean;
};

// Grid positions using Morningstar API numeric codes
// Morningstar style box codes: 1-9 map to 3x3 grid (row-major order)
// 1=Large Value, 2=Large Blend, 3=Large Growth
// 4=Mid Value, 5=Mid Blend, 6=Mid Growth
// 7=Small Value, 8=Small Blend, 9=Small Growth
const STYLE_CODE_TO_POSITION: Record<string, { row: number; col: number }> = {
  "1": { row: 0, col: 0 }, // Large Value
  "2": { row: 0, col: 1 }, // Large Blend
  "3": { row: 0, col: 2 }, // Large Growth
  "4": { row: 1, col: 0 }, // Mid Value
  "5": { row: 1, col: 1 }, // Mid Blend
  "6": { row: 1, col: 2 }, // Mid Growth
  "7": { row: 2, col: 0 }, // Small Value
  "8": { row: 2, col: 1 }, // Small Blend
  "9": { row: 2, col: 2 }, // Small Growth
};

// Size labels
const SIZE_LABELS = ["Large", "Mid", "Small"];
const STYLE_LABELS = ["Value", "Blend", "Growth"];

export function EquityStyleGrid({
  data,
  timestamp,
  language = "en_GB",
  coverage,
  showLegend = true,
}: EquityStyleGridProps) {
  const primaryColor = useBrandColor() ?? "#ee8438";
  const t = pickT(T, language);

  // Translated labels
  const SIZE_LABELS_T = [t.large, t.mid, t.small];
  const STYLE_LABELS_T = [t.value, t.blend, t.growth];

  // Create a 3x3 grid with percentages
  const grid: number[][] = Array(3)
    .fill(0)
    .map(() => Array(3).fill(0));

  // Populate grid from data (style is Morningstar code like "1", "2", etc.)
  data.forEach((item) => {
    const position = STYLE_CODE_TO_POSITION[item.style];
    if (position) {
      grid[position.row][position.col] = item.percentage;
    }
  });

  // Get fill color based on percentage (darker = higher percentage)
  // Using the theme color with varying opacity
  const getFillColor = (percentage: number): string => {
    if (percentage === 0) return "transparent";
    if (percentage < 5) return `${primaryColor}1A`; // 10% opacity
    if (percentage < 10) return `${primaryColor}33`; // 20% opacity
    if (percentage < 20) return `${primaryColor}66`; // 40% opacity
    if (percentage < 30) return `${primaryColor}99`; // 60% opacity
    return `${primaryColor}CC`; // 80% opacity
  };

  // Get text color based on background
  const getTextColor = (percentage: number): string => {
    if (percentage >= 20) return "#ffffff";
    return "currentColor";
  };

  // SVG dimensions
  const cellSize = 60;
  const labelWidth = 50;
  const labelHeight = 25;
  const totalWidth = labelWidth + cellSize * 3;
  const totalHeight = labelHeight + cellSize * 3;

  return (
    <div className="w-full space-y-3">
      <div className="flex justify-center">
        <svg
          viewBox={`0 0 ${totalWidth} ${totalHeight}`}
          className="w-full max-w-sm"
          style={{ maxHeight: "300px" }}
          role="img"
          aria-label={t.equityStyleGridAria}
        >
          <title>{t.equityStyleGridTitle}</title>
          {/* Column headers (Value, Blend, Growth) */}
          {STYLE_LABELS_T.map((label, index) => (
            <text
              key={`col-${label}`}
              x={labelWidth + index * cellSize + cellSize / 2}
              y={labelHeight / 2}
              textAnchor="middle"
              dominantBaseline="middle"
              className="text-xs fill-current text-muted-foreground"
            >
              {label}
            </text>
          ))}

          {/* Row headers (Large, Mid, Small) */}
          {SIZE_LABELS_T.map((label, index) => (
            <text
              key={`row-${label}`}
              x={labelWidth / 2}
              y={labelHeight + index * cellSize + cellSize / 2}
              textAnchor="middle"
              dominantBaseline="middle"
              className="text-xs fill-current text-muted-foreground"
            >
              {label}
            </text>
          ))}

          {/* Grid cells */}
          {grid.map((row, rowIndex) =>
            row.map((percentage, colIndex) => {
              const x = labelWidth + colIndex * cellSize;
              const y = labelHeight + rowIndex * cellSize;
              const fillColor = getFillColor(percentage);
              const textColor = getTextColor(percentage);

              return (
                <g key={`cell-${SIZE_LABELS[rowIndex]}-${STYLE_LABELS[colIndex]}`}>
                  {/* Cell background */}
                  <rect
                    x={x}
                    y={y}
                    width={cellSize}
                    height={cellSize}
                    fill={fillColor}
                    stroke="currentColor"
                    strokeWidth="1"
                    className="stroke-border"
                  />
                  {/* Percentage text */}
                  {percentage > 0 && (
                    <text
                      x={x + cellSize / 2}
                      y={y + cellSize / 2}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      className="text-lg font-bold"
                      fill={textColor}
                    >
                      {percentage.toFixed(0)}
                    </text>
                  )}
                </g>
              );
            }),
          )}
        </svg>
      </div>

      {showLegend && (
        <div className="text-xs text-muted-foreground">
          <div className="font-medium mb-1">{t.weightLegend}</div>
          <div className="flex flex-wrap gap-2">
            <div className="flex items-center gap-1">
              <div
                className="w-3 h-3 border"
                style={{ backgroundColor: `${primaryColor}1A` }}
              ></div>
              <span>&lt; 5%</span>
            </div>
            <div className="flex items-center gap-1">
              <div
                className="w-3 h-3 border"
                style={{ backgroundColor: `${primaryColor}33` }}
              ></div>
              <span>5-10%</span>
            </div>
            <div className="flex items-center gap-1">
              <div
                className="w-3 h-3 border"
                style={{ backgroundColor: `${primaryColor}66` }}
              ></div>
              <span>10-20%</span>
            </div>
            <div className="flex items-center gap-1">
              <div
                className="w-3 h-3 border"
                style={{ backgroundColor: `${primaryColor}99` }}
              ></div>
              <span>20-30%</span>
            </div>
            <div className="flex items-center gap-1">
              <div
                className="w-3 h-3 border"
                style={{ backgroundColor: `${primaryColor}CC` }}
              ></div>
              <span>&gt; 30%</span>
            </div>
          </div>
        </div>
      )}

      {(coverage || timestamp) && (
        <div className="text-xs text-muted-foreground">
          {coverage && coverage.originalWeightPct < 99.995 && (
            <div>{t.missingDataNote(String(Math.round(coverage.originalWeightPct)))}</div>
          )}
          {timestamp && <div>{t.snapshotTaken(getRelativeTime(timestamp, language))}</div>}
        </div>
      )}
    </div>
  );
}
